import {
  Text,
  Paper,
  Flex,
  Group,
  Badge,
  Table,
  Box,
  Button,
  Progress,
  Stack,
  Stepper,
  Select,
} from '@mantine/core';
import {
  IconUpload,
  IconFile,
  IconCheck,
  IconX,
  IconCloudUpload,
  IconCpu,
  IconCircleCheck,
} from '@tabler/icons-react';
import { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from './api';

const TIPOS_ARCHIVO = [
  { tipo: 'MDI', label: 'Capacidades (MDI)', accept: '.csv,.xlsx' },
  { tipo: 'ITINERARIO', label: 'Itinerario', accept: '.csv,.xlsx' },
  { tipo: 'FOC', label: 'Factor de Ocupación (FOC)', accept: '.csv,.xlsx' },
];

const MESES_OPTIONS = [
  { value: '1', label: 'ENERO' },
  { value: '2', label: 'FEBRERO' },
  { value: '3', label: 'MARZO' },
  { value: '4', label: 'ABRIL' },
  { value: '5', label: 'MAYO' },
  { value: '6', label: 'JUNIO' },
  { value: '7', label: 'JULIO' },
  { value: '8', label: 'AGOSTO' },
  { value: '9', label: 'SEPTIEMBRE' },
  { value: '10', label: 'OCTUBRE' },
  { value: '11', label: 'NOVIEMBRE' },
  { value: '12', label: 'DICIEMBRE' },
];

const getYearOptions = () => {
  const y = new Date().getFullYear();

  return Array.from(
    { length: 5 },
    (_, i) => ({
      value: String(y - 2 + i),
      label: String(y - 2 + i),
    }),
  );
};

const ESTATUS_COLORS = {
  PENDIENTE: 'yellow',
  SUBIDO: 'blue',
  EN_COLA: 'blue',
  PROCESANDO: 'blue',
  COMPLETADO: 'green',
  ERROR: 'red',
  ERROR_SUBIDA: 'red',
  NO_INICIADO: 'gray',
};

/*
 * IMPORTANTE:
 * Sustituye este hostname por el hostname REAL que utiliza
 * tu presigned URL de S3.
 *
 * Ejemplo:
 * mi-bucket.s3.us-east-1.amazonaws.com
 */
const ALLOWED_UPLOAD_HOSTS = [
  'amx-ssa-reportes-dc-sscn-us-east-1-045155393942-pd.s3.us-east-1.amazonaws.com',
];

/**
 * Valida que la URL de carga:
 * - Sea un string válido
 * - Utilice HTTPS
 * - Apunte únicamente a un hostname autorizado
 */
const validateUploadUrl = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error('URL de carga inválida');
  }

  let parsedUrl;

  try {
    parsedUrl = new URL(value);
  } catch {
    throw new Error('URL de carga inválida');
  }

  if (parsedUrl.protocol !== 'https:') {
    throw new Error('El protocolo de la URL de carga no está permitido');
  }

  if (!ALLOWED_UPLOAD_HOSTS.includes(parsedUrl.hostname)) {
    throw new Error('El destino de la carga no está autorizado');
  }

  return parsedUrl.href;
};

/**
 * Calcula el step activo del stepper:
 * 0 = Subiendo archivo
 * 1 = Procesando
 * 3 = Finalizado
 * -1 = Error
 */
function getActiveStep(upload) {
  if (upload.estatusProceso === 'COMPLETADO') return 3;

  if (
    upload.estatusProceso === 'ERROR'
    || upload.estatusSubida === 'ERROR_SUBIDA'
  ) {
    return -1;
  }

  if (['EN_COLA', 'PROCESANDO'].includes(upload.estatusProceso)) {
    return 1;
  }

  return 0;
}

export default function CargaArchivos() {
  const { user } = useAuth0();

  const [historial, setHistorial] = useState([]);
  const [activeUploads, setActiveUploads] = useState([]);

  const fileInputRefs = useRef({});
  const pollingRefs = useRef({});

  const now = new Date();

  const [uploadMes, setUploadMes] = useState(
    String(now.getMonth() + 1),
  );

  const [uploadAnio, setUploadAnio] = useState(
    String(now.getFullYear()),
  );

  useEffect(() => {
    loadHistorial();

    return () => {
      Object.values(pollingRefs.current).forEach(clearInterval);
    };
  }, []);

  const loadHistorial = async () => {
    try {
      const res = await api.get('/upload/historial?limit=15');
      setHistorial(res.data);
    } catch (e) {
      console.warn(
        'No se pudo cargar historial:',
        e.message,
      );
    }
  };

  const handleUpload = async (tipo) => {
    const input = fileInputRefs.current[tipo];

    if (!input?.files?.length) {
      return;
    }

    const file = input.files[0];

    const uploadEntry = {
      id: null,
      tipo,
      nombre: file.name,
      estatusSubida: 'PENDIENTE',
      estatusProceso: 'NO_INICIADO',
      uploadProgress: 0,
      processProgress: 0,
      error: null,
      registrosOk: null,
      registrosError: null,
      logs: [],
    };

    setActiveUploads((prev) => [
      ...prev,
      uploadEntry,
    ]);

    try {
      // Paso 1: Obtener Presigned URL
      const presignedRes = await api.post(
        '/upload/presigned-url',
        {
          tipo,
          fileName: file.name,
          mes: parseInt(uploadMes, 10),
          anio: parseInt(uploadAnio, 10),
          usuarioEmail:
            user?.email || 'sistema@aeromexico.com',
        },
      );

      const {
        uploadId,
        url,
      } = presignedRes.data;

      // Validar URL antes de utilizarla como destino HTTP
      const validatedUrl = validateUploadUrl(url);

      uploadEntry.id = uploadId;

      updateActiveUpload(
        uploadId,
        {
          uploadProgress: 5,
        },
      );

      // Paso 2: Subir a S3
      await uploadToS3WithProgress(
        uploadId,
        validatedUrl,
        file,
      );

      updateActiveUpload(
        uploadId,
        {
          estatusSubida: 'SUBIDO',
          uploadProgress: 100,
        },
      );

      // Paso 3: Confirmar + SQS
      await api.post(
        '/upload/confirm',
        {
          uploadId,
          tamanoBytes: file.size,
        },
      );

      updateActiveUpload(
        uploadId,
        {
          estatusProceso: 'EN_COLA',
          processProgress: 10,
        },
      );

      // Paso 4: Polling del procesamiento
      startPolling(uploadId);
    } catch (error) {
      const uploadId = uploadEntry.id;

      const errorMsg =
        error.response?.data?.message
        || error.message;

      updateActiveUpload(
        uploadId || `temp_${tipo}`,
        {
          estatusSubida:
            uploadEntry.estatusSubida === 'SUBIDO'
              ? 'SUBIDO'
              : 'ERROR_SUBIDA',
          estatusProceso: 'ERROR',
          error: errorMsg,
        },
      );

      if (uploadId) {
        try {
          await api.post(
            '/upload/error',
            {
              uploadId,
              mensajeError: errorMsg,
            },
          );
        } catch (e) {
          // No hacer nada
        }
      }
    }

    input.value = '';
  };

  /**
   * Sube archivo a S3 utilizando XMLHttpRequest
   * para obtener progreso real de la carga.
   */
  const uploadToS3WithProgress = (
    uploadId,
    validatedUrl,
    file,
  ) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // validatedUrl ya fue validada antes de llegar aquí.
    xhr.open('PUT', validatedUrl, true);

    xhr.setRequestHeader(
      'Content-Type',
      file.type
        || 'application/octet-stream',
    );

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percent = Math.round(
          (event.loaded / event.total) * 100,
        );

        updateActiveUpload(
          uploadId,
          {
            uploadProgress: percent,
          },
        );
      }
    };

    xhr.onload = () => {
      if (
        xhr.status >= 200
        && xhr.status < 300
      ) {
        resolve();
      } else {
        reject(
          new Error(
            `Error S3: ${xhr.status} ${xhr.statusText}`,
          ),
        );
      }
    };

    xhr.onerror = () => {
      reject(
        new Error(
          'Error de red al subir archivo',
        ),
      );
    };

    xhr.send(file);
  });

  const updateActiveUpload = (
    uploadId,
    updates,
  ) => {
    setActiveUploads((prev) =>
      prev.map((u) =>
        u.id === uploadId
          ? {
            ...u,
            ...updates,
          }
          : u,
      ),
    );
  };

  const startPolling = (uploadId) => {
    let attempts = 0;

    const MAX_ATTEMPTS = 360;

    const interval = setInterval(
      async () => {
        attempts++;

        try {
          const res = await api.get(
            `/upload/status/${uploadId}`,
          );

          const data = res.data;

          let processProgress = 10;

          if (
            data.estatusProceso === 'PROCESANDO'
          ) {
            processProgress = 50;
          }

          if (
            data.estatusProceso === 'COMPLETADO'
          ) {
            processProgress = 100;
          }

          if (
            data.estatusProceso === 'ERROR'
          ) {
            processProgress = 100;
          }

          // Parsear logs
          let logs = [];

          if (data.logProceso) {
            try {
              logs = JSON.parse(
                data.logProceso,
              );
            } catch (e) {
              // Ignorar log inválido
            }
          }

          updateActiveUpload(
            uploadId,
            {
              estatusSubida:
                data.estatusSubida,

              estatusProceso:
                data.estatusProceso,

              processProgress,

              registrosOk:
                data.registrosOk,

              registrosError:
                data.registrosError,

              error:
                data.mensajeError,

              logs,
            },
          );

          if (data.terminado) {
            clearInterval(interval);

            delete pollingRefs.current[
              uploadId
            ];

            loadHistorial();
          } else if (
            attempts >= MAX_ATTEMPTS
          ) {
            clearInterval(interval);

            delete pollingRefs.current[
              uploadId
            ];

            updateActiveUpload(
              uploadId,
              {
                estatusProceso: 'ERROR',
                processProgress: 100,
                error:
                  'Timeout: el procesamiento tardó demasiado. Revisa el historial.',
              },
            );

            loadHistorial();
          }
        } catch (e) {
          console.warn(
            'Polling error:',
            e.message,
          );

          if (
            attempts >= MAX_ATTEMPTS
          ) {
            clearInterval(interval);

            delete pollingRefs.current[
              uploadId
            ];

            updateActiveUpload(
              uploadId,
              {
                estatusProceso: 'ERROR',
                processProgress: 100,
                error:
                  'No se pudo verificar el estatus.',
              },
            );
          }
        }
      },
      2500,
    );

    pollingRefs.current[uploadId] =
      interval;
  };

  const dismissUpload = (uploadId) => {
    setActiveUploads((prev) =>
      prev.filter(
        (u) => u.id !== uploadId,
      ),
    );

    if (pollingRefs.current[uploadId]) {
      clearInterval(
        pollingRefs.current[uploadId],
      );

      delete pollingRefs.current[
        uploadId
      ];
    }
  };

  return (
    <>
      <Flex
        justify="space-between"
        align="center"
        mb="md"
      >
        <Text
          fw={700}
          size="xl"
          c="#020C41"
        >
          Carga de Archivos
        </Text>
      </Flex>

      {/* Upload zones */}
      <Paper
        shadow="xs"
        p="md"
        mb="md"
        radius="md"
      >
        <Text
          size="sm"
          fw={600}
          c="#020C41"
          mb="sm"
        >
          Seleccionar archivos para cargar
        </Text>

        <Flex gap="md" wrap="wrap">
          {TIPOS_ARCHIVO.map(
            ({
              tipo,
              label,
              accept,
            }) => (
              <Paper
                key={tipo}
                withBorder
                p="md"
                radius="md"
                style={{
                  flex: 1,
                  minWidth: 200,
                  textAlign: 'center',
                }}
              >
                <IconFile
                  size={32}
                  color="#030D75"
                  style={{
                    marginBottom: 8,
                  }}
                />

                <Text
                  size="sm"
                  fw={600}
                  c="#020C41"
                  mb="xs"
                >
                  {label}
                </Text>

                {tipo === 'FOC' && (
                  <Flex
                    gap="xs"
                    mb="xs"
                    justify="center"
                  >
                    <Select
                      placeholder="Mes"
                      value={uploadMes}
                      onChange={(v) =>
                        v && setUploadMes(v)
                      }
                      data={MESES_OPTIONS}
                      size="xs"
                      allowDeselect={false}
                      style={{
                        width: 110,
                      }}
                    />

                    <Select
                      placeholder="Año"
                      value={uploadAnio}
                      onChange={(v) =>
                        v && setUploadAnio(v)
                      }
                      data={getYearOptions()}
                      size="xs"
                      allowDeselect={false}
                      style={{
                        width: 80,
                      }}
                    />
                  </Flex>
                )}

                <input
                  ref={(el) => {
                    fileInputRefs.current[tipo] =
                      el;
                  }}
                  type="file"
                  accept={accept}
                  style={{
                    display: 'none',
                  }}
                  onChange={() =>
                    handleUpload(tipo)
                  }
                />

                <Button
                  size="xs"
                  color="#030D75"
                  leftSection={
                    <IconUpload size={14} />
                  }
                  onClick={() =>
                    fileInputRefs.current[
                      tipo
                    ]?.click()
                  }
                >
                  Subir {tipo}
                </Button>
              </Paper>
            ),
          )}
        </Flex>
      </Paper>

      {/* Active uploads */}
      {activeUploads.length > 0 && (
        <Paper
          shadow="xs"
          p="md"
          mb="md"
          radius="md"
        >
          <Text
            size="sm"
            fw={600}
            c="#020C41"
            mb="md"
          >
            Cargas en progreso
          </Text>

          <Stack gap="md">
            {activeUploads.map((upload) => {
              const activeStep =
                getActiveStep(upload);

              const isError =
                activeStep === -1;

              const isDone =
                activeStep === 3;

              const errorStep = isError
                ? (
                  upload.estatusSubida
                  === 'ERROR_SUBIDA'
                    ? 0
                    : 1
                )
                : -1;

              return (
                <Box
                  key={
                    upload.id
                    || upload.tipo
                  }
                  p="md"
                  style={{
                    border:
                      `1px solid ${
                        isError
                          ? '#FA0073'
                          : isDone
                            ? '#2da44e'
                            : '#e0e0e0'
                      }`,
                    borderRadius: 8,
                    backgroundColor:
                      isError
                        ? '#FFF5F8'
                        : isDone
                          ? '#F6FFF6'
                          : '#FAFBFF',
                  }}
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    mb="sm"
                  >
                    <Group gap="xs">
                      <Badge
                        size="sm"
                        color="#030D75"
                        variant="light"
                      >
                        {upload.tipo}
                      </Badge>

                      <Text
                        size="sm"
                        fw={600}
                        c="#020C41"
                      >
                        {upload.nombre}
                      </Text>
                    </Group>

                    {(isDone || isError) && (
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        color="gray"
                        onClick={() =>
                          dismissUpload(
                            upload.id,
                          )
                        }
                      >
                        ✕
                      </Button>
                    )}
                  </Flex>

                  <Stepper
                    active={
                      isError
                        ? errorStep
                        : isDone
                          ? 3
                          : activeStep
                    }
                    color={
                      isError
                        ? 'red'
                        : '#030D75'
                    }
                    size="sm"
                    iconSize={28}
                  >
                    <Stepper.Step
                      label="Subir archivo"
                      description={
                        activeStep === 0
                          ? `${upload.uploadProgress}%`
                          : isError
                            && errorStep === 0
                            ? 'Error'
                            : 'Completado'
                      }
                      icon={
                        <IconCloudUpload
                          size={16}
                        />
                      }
                      color={
                        isError
                          && errorStep === 0
                          ? 'red'
                          : '#030D75'
                      }
                      completedIcon={
                        isError
                          && errorStep === 0
                          ? (
                            <IconX size={16} />
                          )
                          : (
                            <IconCheck
                              size={16}
                            />
                          )
                      }
                    >
                      {activeStep === 0
                        && !isError && (
                          <Progress
                            value={
                              upload.uploadProgress
                            }
                            color="#030D75"
                            size="md"
                            animated
                            mt="xs"
                            radius="md"
                          />
                        )}
                    </Stepper.Step>

                    <Stepper.Step
                      label="Procesando"
                      description={
                        activeStep === 1
                          ? upload.estatusProceso
                            === 'PROCESANDO'
                            ? 'En ejecución...'
                            : 'En cola...'
                          : isError
                            && errorStep === 1
                            ? 'Error'
                            : isDone
                              ? 'Completado'
                              : 'Pendiente'
                      }
                      icon={
                        <IconCpu size={16} />
                      }
                      color={
                        isError
                          && errorStep === 1
                          ? 'red'
                          : '#030D75'
                      }
                      completedIcon={
                        isError
                          && errorStep === 1
                          ? (
                            <IconX size={16} />
                          )
                          : (
                            <IconCheck
                              size={16}
                            />
                          )
                      }
                    >
                      {activeStep === 1
                        && !isError && (
                          <Progress
                            value={
                              upload.processProgress
                            }
                            color="#030D75"
                            size="md"
                            animated
                            mt="xs"
                            radius="md"
                          />
                        )}

                      {upload.logs
                        && upload.logs.length > 0 && (
                          <Box
                            mt="xs"
                            p="xs"
                            style={{
                              backgroundColor:
                                '#F8F9FA',
                              borderRadius: 4,
                              maxHeight: 120,
                              overflowY: 'auto',
                            }}
                          >
                            {upload.logs.map(
                              (log, idx) => (
                                <Text
                                  key={idx}
                                  size="xs"
                                  c={
                                    log.msg.startsWith(
                                      '✓',
                                    )
                                      ? 'green'
                                      : '#555'
                                  }
                                  style={{
                                    fontFamily:
                                      'monospace',
                                    lineHeight: 1.6,
                                  }}
                                >
                                  → {log.msg}
                                </Text>
                              ),
                            )}
                          </Box>
                        )}
                    </Stepper.Step>

                    <Stepper.Step
                      label="Finalizado"
                      description={
                        isDone
                          ? `${upload.registrosOk || 0} registros`
                          : ''
                      }
                      icon={
                        <IconCircleCheck
                          size={16}
                        />
                      }
                      color={
                        isDone
                          ? 'green'
                          : '#030D75'
                      }
                      completedIcon={
                        <IconCheck size={16} />
                      }
                    />
                  </Stepper>

                  {upload.error && (
                    <Paper
                      p="xs"
                      mt="sm"
                      radius="sm"
                      style={{
                        backgroundColor:
                          '#FFF0F5',
                        border:
                          '1px solid #FA0073',
                      }}
                    >
                      <Text
                        size="xs"
                        c="#FA0073"
                        fw={500}
                      >
                        ⚠ {upload.error}
                      </Text>
                    </Paper>
                  )}

                  {isDone && (
                    <Paper
                      p="xs"
                      mt="sm"
                      radius="sm"
                      style={{
                        backgroundColor:
                          '#F0FFF4',
                        border:
                          '1px solid #2da44e',
                      }}
                    >
                      <Text
                        size="xs"
                        c="green"
                        fw={500}
                      >
                        ✓ Archivo procesado:{' '}
                        {upload.registrosOk || 0}{' '}
                        registros OK
                        {upload.registrosError > 0
                          ? `, ${upload.registrosError} con error`
                          : ''}
                      </Text>
                    </Paper>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Paper>
      )}

      {/* Historial */}
      <Paper
        shadow="xs"
        radius="md"
        style={{
          overflow: 'hidden',
        }}
      >
        <Flex
          justify="space-between"
          align="center"
          p="sm"
          style={{
            backgroundColor: '#030D75',
          }}
        >
          <Text
            size="sm"
            fw={600}
            c="white"
          >
            Historial de Cargas
          </Text>

          <Button
            size="compact-xs"
            variant="subtle"
            color="white"
            onClick={loadHistorial}
          >
            Actualizar
          </Button>
        </Flex>

        <Box
          style={{
            overflowX: 'auto',
          }}
        >
          <Table
            striped
            highlightOnHover
            withTableBorder
            horizontalSpacing="sm"
            verticalSpacing="xs"
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Tipo</Table.Th>
                <Table.Th>Archivo</Table.Th>
                <Table.Th>Usuario</Table.Th>
                <Table.Th>Fecha</Table.Th>
                <Table.Th>Subida</Table.Th>
                <Table.Th>Proceso</Table.Th>
                <Table.Th>Registros</Table.Th>
                <Table.Th>Detalle</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {historial.length === 0 ? (
                <Table.Tr>
                  <Table.Td
                    colSpan={8}
                    style={{
                      textAlign: 'center',
                    }}
                  >
                    <Text
                      size="sm"
                      c="dimmed"
                    >
                      Sin cargas registradas
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                historial.map((row) => (
                  <Table.Tr
                    key={row.uploadId}
                  >
                    <Table.Td>
                      <Badge
                        size="xs"
                        color="am-blue"
                        variant="light"
                      >
                        {row.tipo}
                      </Badge>
                    </Table.Td>

                    <Table.Td>
                      <Text
                        size="xs"
                        fw={500}
                      >
                        {row.nombre}
                      </Text>
                    </Table.Td>

                    <Table.Td>
                      <Text
                        size="xs"
                        c="dimmed"
                      >
                        {row.usuario || '—'}
                      </Text>
                    </Table.Td>

                    <Table.Td>
                      <Text size="xs">
                        {row.fechaSolicitud
                          ? new Date(
                            row.fechaSolicitud,
                          ).toLocaleString(
                            'es-MX',
                            {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            },
                          )
                          : '—'}
                      </Text>
                    </Table.Td>

                    <Table.Td>
                      <Badge
                        size="xs"
                        color={
                          ESTATUS_COLORS[
                            row.estatusSubida
                          ]
                        }
                        variant="dot"
                      >
                        {row.estatusSubida}
                      </Badge>
                    </Table.Td>

                    <Table.Td>
                      <Badge
                        size="xs"
                        color={
                          ESTATUS_COLORS[
                            row.estatusProceso
                          ]
                        }
                        variant="filled"
                      >
                        {row.estatusProceso}
                      </Badge>
                    </Table.Td>

                    <Table.Td>
                      {row.registrosOk !== null ? (
                        <Group gap={4}>
                          <Text
                            size="xs"
                            c="green"
                            fw={600}
                          >
                            {row.registrosOk} ✓
                          </Text>

                          {row.registrosError > 0
                            && (
                              <Text
                                size="xs"
                                c="red"
                              >
                                {row.registrosError} ✗
                              </Text>
                            )}
                        </Group>
                      ) : (
                        <Text
                          size="xs"
                          c="dimmed"
                        >
                          —
                        </Text>
                      )}
                    </Table.Td>

                    <Table.Td
                      style={{
                        maxWidth: 280,
                      }}
                    >
                      {row.mensajeError ? (
                        <Text
                          size="xs"
                          c="#FA0073"
                          lineClamp={2}
                          title={
                            row.mensajeError
                          }
                        >
                          ⚠ {row.mensajeError}
                        </Text>
                      ) : row.estatusProceso
                        === 'COMPLETADO' ? (
                        <Text
                          size="xs"
                          c="green"
                        >
                          ✓ OK
                        </Text>
                      ) : (
                        <Text
                          size="xs"
                          c="dimmed"
                        >
                          —
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Box>
      </Paper>
    </>
  );
}
